import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import config from '../../config';
import { AssignmentSolution, Pipeline, Result } from './types';
import { RootState } from '../store';
import { AssignmentScore } from '../service/types';

export const dataApi = createApi({
  reducerPath: 'data',
  baseQuery: fetchBaseQuery({
    baseUrl: config.dataBackend,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).usertoken.token;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['data'],
  endpoints: builder => ({
    getPipelines: builder.query<Pipeline[], void>({
      query: () => ({ url: `/pipelines/` }),
      providesTags: ['data'],
    }),
    savePipeline: builder.mutation<undefined, { pid: string; pipeline: Pipeline }>({
      query: ({ pid, pipeline }) => ({ url: `/pipelines/${pid}`, method: 'PUT', body: { ...pipeline } }),
      invalidatesTags: ['data'],
    }),
    deletePipeline: builder.mutation<undefined, string | undefined>({
      query: pid => ({url: `/pipelines/${pid}`, method: 'DELETE'}),
      invalidatesTags: ['data'],
    }),
    getResult: builder.query<Result[], { rid: string | undefined }>({
      query: ({rid}) => ({url: `/results/${rid}`, method: 'GET'}),
      providesTags: ['data'],
    }),
    getServiceResult: builder.query<Result, { rid: string | undefined; sid: string | undefined }>({
      query: ({rid, sid}) => ({url: `/results/${rid}/?sid=${sid}`, method: 'GET'}),
      providesTags: ['data'],
    }),
    getResults: builder.query<Result[], void>({
      query: () => ({url: `/results/`, method: 'GET'}),
      providesTags: ['data'],
      keepUnusedDataFor: 0,
    }),
    deleteResult: builder.mutation<undefined, string | undefined>({
      query: rid => ({url: `/results/${rid}`, method: 'DELETE'}),
      invalidatesTags: ['data'],
    }),
    getNewToken: builder.query<string, void>({
      query: () => ({ url: `/user/new/`}),
    }),
    authToken: builder.mutation<any, string>({
      async queryFn(arg, queryApi, extraOptions, baseQuery) {
        const result = await baseQuery({ url: `/user/${arg}/`, method: 'GET'})

        if (result.error) {
          return { error: result.error }
        }
        return { data: result.data }
      },
    }),
    getAssignmentUserSolution: builder.query<AssignmentSolution, string>({
      query: (assignment_title) => ({ url: `/assignments/${assignment_title}/`, method: 'GET'}),
    }),
    getUserAssignmentScores: builder.query<AssignmentScore[], void>({
      query: () => ( { url: '/scores/', method: 'GET'} ),
      keepUnusedDataFor: 0,
    }),
    deleteResults: builder.mutation<undefined, string[]>({
      query: (rids) => ({url: '/results/', method: 'DELETE', body: rids}),
      invalidatesTags: ['data'],
    }),
    deletePipelines: builder.mutation<undefined, string[]>({
      query: (pids) => ({url: '/pipelines/', method: 'DELETE', body: pids}),
      invalidatesTags: ['data'],
    }),
  }),
})

export const { useGetResultsQuery, useGetResultQuery, useGetServiceResultQuery, useGetPipelinesQuery, useSavePipelineMutation, useDeletePipelineMutation, useDeleteResultMutation, useLazyGetNewTokenQuery, useAuthTokenMutation, useLazyGetAssignmentUserSolutionQuery, useGetUserAssignmentScoresQuery, useDeleteResultsMutation, useDeletePipelinesMutation } = dataApi;